import { useState, useEffect, useCallback, useMemo } from "react";
import api, { formatApiError, isAuthError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Phone, Mail, Search, Send, Clock, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const STATUS_LABEL = { new: "New", contacted: "Contacted", quoted: "Quoted", booked: "Booked", lost: "Lost" };
const STATUS_BADGE = {
  new: "bg-sky-100 text-sky-700 border-sky-200",
  contacted: "bg-amber-100 text-amber-700 border-amber-200",
  quoted: "bg-violet-100 text-violet-700 border-violet-200",
  booked: "bg-emerald-100 text-emerald-700 border-emerald-200",
  lost: "bg-slate-200 text-slate-600 border-slate-300",
};
const SOURCE_LABEL = { phone: "Phone", email: "Email", walk_in: "Walk-in", website: "Website", referral: "Referral", other: "Other" };

const EMPTY_FORM = {
  name: "", email: "", phone: "", qualification_id: "",
  interest_text: "", source: "phone", status: "new", notes: "", next_follow_up: "",
};

export default function EnquiriesPage() {
  useDocumentTitle("Enquiries", "Track prospective learners before they enrol.");
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "staff" || user?.role === "superadmin";

  const [enquiries, setEnquiries] = useState([]);
  const [counts, setCounts] = useState({});
  const [dueToday, setDueToday] = useState(0);
  const [statuses, setStatuses] = useState(["new", "contacted", "quoted", "booked", "lost"]);
  const [sources, setSources] = useState(["phone", "email", "walk_in", "website", "referral", "other"]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [quals, setQuals] = useState([]);
  const [courses, setCourses] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [detail, setDetail] = useState(null); // selected enquiry for view/chase
  const [chaseDraft, setChaseDraft] = useState({ method: "call", outcome: "", next_follow_up: "", new_status: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bookCourseId, setBookCourseId] = useState("");

  const fetchEnquiries = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search.trim()) params.set("search", search.trim());
      const { data } = await api.get(`/enquiries?${params.toString()}`);
      setEnquiries(data.enquiries);
      setCounts(data.counts || {});
      setDueToday(data.due_today || 0);
      if (data.statuses?.length) setStatuses(data.statuses);
      if (data.sources?.length) setSources(data.sources);
    } catch (err) {
      if (!isAuthError(err)) toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    api.get("/qualifications").then(r => setQuals(r.data)).catch(() => {});
    api.get("/courses?status=scheduled&limit=200").then(r => setCourses(r.data.courses || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchEnquiries(); }, [fetchEnquiries]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name, email: e.email || "", phone: e.phone || "",
      qualification_id: e.qualification_id || "",
      interest_text: e.interest_text || "",
      source: e.source, status: e.status,
      notes: e.notes || "",
      next_follow_up: e.next_follow_up || "",
    });
    setDialogOpen(true);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const payload = { ...form };
    if (!payload.email) delete payload.email;
    if (!payload.qualification_id) delete payload.qualification_id;
    try {
      if (editing) {
        await api.put(`/enquiries/${editing.id}`, payload);
        toast.success("Enquiry updated");
      } else {
        await api.post("/enquiries", payload);
        toast.success("Enquiry logged");
      }
      setDialogOpen(false); fetchEnquiries();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeEnquiry = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/enquiries/${deleteTarget.id}`);
      toast.success("Enquiry removed");
      setDeleteTarget(null);
      fetchEnquiries();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDeleteTarget(null); }
  };

  const addChase = async () => {
    if (!detail || !chaseDraft.outcome.trim()) { toast.error("Enter what happened on the call"); return; }
    try {
      const payload = {
        method: chaseDraft.method,
        outcome: chaseDraft.outcome.trim(),
      };
      if (chaseDraft.next_follow_up) payload.next_follow_up = chaseDraft.next_follow_up;
      if (chaseDraft.new_status) payload.new_status = chaseDraft.new_status;
      const { data } = await api.post(`/enquiries/${detail.id}/chase`, payload);
      setDetail(data);
      setChaseDraft({ method: "call", outcome: "", next_follow_up: "", new_status: "" });
      toast.success("Chase logged");
      fetchEnquiries();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const markBooked = async () => {
    if (!detail || !bookCourseId) { toast.error("Pick a course"); return; }
    try {
      const { data } = await api.post(`/enquiries/${detail.id}/book`, { course_id: bookCourseId });
      setDetail(data.enquiry);
      setBookCourseId("");
      toast.success("Booked — enrolment created and welcome email sent");
      fetchEnquiries();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const markLost = async (reason) => {
    if (!detail) return;
    try {
      const { data } = await api.put(`/enquiries/${detail.id}`, { status: "lost", lost_reason: reason });
      setDetail(data);
      toast.success("Enquiry marked as lost");
      fetchEnquiries();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const totalOpen = (counts.new || 0) + (counts.contacted || 0) + (counts.quoted || 0);

  return (
    <div className="space-y-4" data-testid="enquiries-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-semibold">Enquiries</h1>
          <p className="text-xs text-muted-foreground mt-1">Prospective learners who haven't enrolled yet. Log every call, track follow-ups, convert to bookings.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, phone…"
              className="pl-8 h-9 w-64 text-xs"
              data-testid="enquiry-search-input"
            />
          </div>
          {isStaff && (
            <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-enquiry-btn">
              <Plus size={14} className="mr-1" /> Log Enquiry
            </Button>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="enquiry-kpis">
        <KpiTile label="Open" value={totalOpen} color="text-foreground" sub={`${counts.new || 0} new`} testid="kpi-open" />
        <KpiTile label="New" value={counts.new || 0} color="text-sky-600" testid="kpi-new" />
        <KpiTile label="Contacted" value={counts.contacted || 0} color="text-amber-600" testid="kpi-contacted" />
        <KpiTile label="Quoted" value={counts.quoted || 0} color="text-violet-600" testid="kpi-quoted" />
        <KpiTile label="Booked" value={counts.booked || 0} color="text-emerald-600" testid="kpi-booked" />
        <KpiTile label="Due today" value={dueToday} color={dueToday > 0 ? "text-red-600" : "text-foreground"} sub={dueToday > 0 ? "Action required" : "All caught up"} testid="kpi-due-today" />
      </div>

      {/* Status filter */}
      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList className="h-8" data-testid="status-filter-tabs">
          <TabsTrigger value="all" className="text-xs px-3 py-1">All</TabsTrigger>
          {statuses.map(s => (
            <TabsTrigger key={s} value={s} className="text-xs px-3 py-1" data-testid={`filter-${s}`}>
              {STATUS_LABEL[s]} <span className="ml-1 text-muted-foreground">({counts[s] || 0})</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* List */}
      <div className="border border-border rounded-md bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left">
            <th className="px-3 py-2.5 font-medium text-muted-foreground text-xs">Name</th>
            <th className="px-3 py-2.5 font-medium text-muted-foreground text-xs hidden md:table-cell">Contact</th>
            <th className="px-3 py-2.5 font-medium text-muted-foreground text-xs">Interest</th>
            <th className="px-3 py-2.5 font-medium text-muted-foreground text-xs">Source</th>
            <th className="px-3 py-2.5 font-medium text-muted-foreground text-xs">Status</th>
            <th className="px-3 py-2.5 font-medium text-muted-foreground text-xs">Follow-up</th>
            <th className="px-3 py-2.5 font-medium text-muted-foreground text-xs hidden lg:table-cell">Logged by</th>
            <th className="px-3 py-2.5 font-medium text-muted-foreground text-xs w-24">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>
            ) : enquiries.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">
                {statusFilter === "all" ? "No enquiries logged yet. Click &apos;Log Enquiry&apos; to add the first one." : `No ${STATUS_LABEL[statusFilter] || statusFilter} enquiries.`}
              </td></tr>
            ) : enquiries.map(e => {
              const overdue = e.next_follow_up && e.next_follow_up <= today && ["new", "contacted", "quoted"].includes(e.status);
              return (
                <tr
                  key={e.id}
                  className="border-b border-border hover:bg-slate-50/60 cursor-pointer"
                  onClick={() => { setDetail(e); setBookCourseId(""); }}
                  data-testid={`enquiry-row-${e.id}`}
                >
                  <td className="px-3 py-2.5 font-medium">{e.name}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground hidden md:table-cell">
                    <div className="flex flex-col gap-0.5">
                      {e.phone && <span className="flex items-center gap-1"><Phone size={10} />{e.phone}</span>}
                      {e.email && <span className="flex items-center gap-1 truncate"><Mail size={10} />{e.email}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    {e.qualification_name && <div className="font-medium">{e.qualification_name}</div>}
                    {e.interest_text && <div className="text-muted-foreground truncate max-w-xs">{e.interest_text}</div>}
                    {!e.qualification_name && !e.interest_text && <span className="text-muted-foreground italic">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{SOURCE_LABEL[e.source] || e.source}</td>
                  <td className="px-3 py-2.5">
                    <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[e.status] || ""}`} data-testid={`enquiry-status-${e.id}`}>
                      {STATUS_LABEL[e.status] || e.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    {e.next_follow_up ? (
                      <span className={`flex items-center gap-1 ${overdue ? "text-red-600 font-medium" : "text-muted-foreground"}`}>
                        {overdue ? <AlertCircle size={11} /> : <Clock size={11} />}
                        {e.next_follow_up}
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground hidden lg:table-cell">{e.created_by_name || "—"}</td>
                  <td className="px-3 py-2.5" onClick={(ev) => ev.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      {isStaff && <button onClick={() => openEdit(e)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`edit-enquiry-${e.id}`}><Pencil size={14} /></button>}
                      {isStaff && <button onClick={() => setDeleteTarget(e)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`delete-enquiry-${e.id}`}><Trash2 size={14} /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="enquiry-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Enquiry" : "Log New Enquiry"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="enquiry-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0131 …" data-testid="enquiry-phone-input" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="enquiry-email-input" /></div>
            </div>
            <div>
              <Label className="text-xs">Interested in qualification</Label>
              <Select value={form.qualification_id || "_none"} onValueChange={(v) => setForm({ ...form, qualification_id: v === "_none" ? "" : v })}>
                <SelectTrigger data-testid="enquiry-qual-select"><SelectValue placeholder="(optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— None / general —</SelectItem>
                  {quals.map(q => <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Free-text interest / notes from call</Label><Input value={form.interest_text} onChange={(e) => setForm({ ...form, interest_text: e.target.value })} placeholder="e.g. wants Gas Safe + Plumbing combo in March" data-testid="enquiry-interest-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger data-testid="enquiry-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{sources.map(s => <SelectItem key={s} value={s}>{SOURCE_LABEL[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="enquiry-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Next follow-up date</Label><Input type="date" value={form.next_follow_up} onChange={(e) => setForm({ ...form, next_follow_up: e.target.value })} data-testid="enquiry-next-fu-input" /></div>
            <div><Label className="text-xs">General notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="enquiry-notes-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="enquiry-submit-btn">{editing ? "Update" : "Log Enquiry"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail / chase-log dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="enquiry-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3">
                  {detail.name}
                  <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[detail.status] || ""}`}>
                    {STATUS_LABEL[detail.status] || detail.status}
                  </Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {detail.phone && <InfoRow icon={Phone} label="Phone" value={detail.phone} />}
                  {detail.email && <InfoRow icon={Mail} label="Email" value={detail.email} />}
                  <InfoRow label="Source" value={SOURCE_LABEL[detail.source] || detail.source} />
                  <InfoRow label="Logged by" value={detail.created_by_name || "—"} />
                  {detail.qualification_name && <InfoRow label="Interested in" value={detail.qualification_name} />}
                  {detail.next_follow_up && <InfoRow label="Next follow-up" value={detail.next_follow_up} />}
                </div>
                {detail.interest_text && (
                  <div className="border-l-4 border-sky-400 bg-sky-50/50 px-3 py-2 text-sm">{detail.interest_text}</div>
                )}
                {detail.notes && <div className="text-xs text-muted-foreground border border-border rounded p-2 bg-slate-50/50">📝 {detail.notes}</div>}
                {detail.lost_reason && <div className="text-xs text-red-700 border border-red-200 rounded p-2 bg-red-50">Lost: {detail.lost_reason}</div>}

                {/* Chase log */}
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Chase log</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="chase-log">
                    {(detail.chase_log || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">No chase entries yet. Log the first call/email below.</p>
                    ) : (
                      [...(detail.chase_log || [])].reverse().map(c => (
                        <div key={c.id} className="border-l-2 border-slate-300 pl-3 py-1 text-xs" data-testid={`chase-entry-${c.id}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{c.method}</span>
                            <span className="text-muted-foreground">{c.at?.slice(0, 16).replace("T", " ")} · {c.by_user_name}</span>
                          </div>
                          <p className="text-foreground mt-0.5 whitespace-pre-wrap">{c.outcome}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Add chase entry */}
                {isStaff && (
                  <div className="border border-border rounded-md p-3 space-y-2 bg-slate-50/40" data-testid="add-chase-block">
                    <h4 className="text-xs font-semibold">Log a chase</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={chaseDraft.method} onValueChange={(v) => setChaseDraft({ ...chaseDraft, method: v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid="chase-method-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["call", "email", "sms", "voicemail", "meeting"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={chaseDraft.new_status || "_keep"} onValueChange={(v) => setChaseDraft({ ...chaseDraft, new_status: v === "_keep" ? "" : v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid="chase-new-status-select"><SelectValue placeholder="Keep current status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_keep">— Keep current status —</SelectItem>
                          {statuses.map(s => <SelectItem key={s} value={s}>Set status → {STATUS_LABEL[s]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      placeholder="What happened on the call? (e.g. left voicemail; wants quote for combo)"
                      value={chaseDraft.outcome}
                      onChange={(e) => setChaseDraft({ ...chaseDraft, outcome: e.target.value })}
                      className="text-xs"
                      data-testid="chase-outcome-input"
                    />
                    <div className="flex items-center gap-2">
                      <Label className="text-[10px] text-muted-foreground">Next follow-up</Label>
                      <Input type="date" value={chaseDraft.next_follow_up} onChange={(e) => setChaseDraft({ ...chaseDraft, next_follow_up: e.target.value })} className="h-8 text-xs w-40" data-testid="chase-next-fu-input" />
                      <div className="flex-1" />
                      <Button size="sm" className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white" onClick={addChase} data-testid="add-chase-submit"><Send size={12} className="mr-1" /> Log Chase</Button>
                    </div>
                  </div>
                )}

                {/* Convert to booking */}
                {isStaff && detail.status !== "booked" && detail.status !== "lost" && (
                  <div className="border border-emerald-200 rounded-md p-3 space-y-2 bg-emerald-50/40" data-testid="convert-booking-block">
                    <h4 className="text-xs font-semibold text-emerald-700">Convert to booking</h4>
                    <div className="flex items-center gap-2">
                      <Select value={bookCourseId} onValueChange={setBookCourseId}>
                        <SelectTrigger className="h-8 text-xs flex-1" data-testid="book-course-select"><SelectValue placeholder="Pick a scheduled course" /></SelectTrigger>
                        <SelectContent>
                          {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title} · {c.start_date}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={markBooked} data-testid="mark-booked-btn">Mark Booked</Button>
                    </div>
                  </div>
                )}

                {/* Mark lost */}
                {isStaff && detail.status !== "lost" && detail.status !== "booked" && (
                  <div className="flex justify-end">
                    <Button
                      size="sm" variant="outline"
                      className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => {
                        const reason = window.prompt("Why was this enquiry lost? (e.g. went elsewhere, budget, timing)") || "";
                        if (reason !== null) markLost(reason);
                      }}
                      data-testid="mark-lost-btn"
                    >
                      Mark as lost
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-enquiry-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this enquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to remove the enquiry for <strong>{deleteTarget?.name}</strong> and its chase history. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeEnquiry} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-enquiry-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function KpiTile({ label, value, color, sub, testid }) {
  return (
    <div className="border border-border rounded-md bg-white p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-2xl font-semibold ${color || ""}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-medium flex items-center gap-1">{Icon && <Icon size={11} />}{value}</p>
    </div>
  );
}
